/* Created by Interactive 63 - https://interactive63.com */
/**
 * Default values and constructors for project data: scene and export settings, the initial
 * editor state, scene-name derivation and an empty project.
 */
import { createId } from './ids';
import { defaultMediaSettings } from './media';
import type { Easing, EditorState, ExportSettings, Project, SceneSettings } from './types';

const MAX_SCENE_NAME_LENGTH = 32;

/** Returns the default scene settings for a new project. */
export function defaultSceneSettings(): SceneSettings {
  return {
    duration: 2000,
    delay: 0,
    iterations: 1,
    direction: 'normal',
    fillMode: 'both',
    trigger: { type: 'auto' },
    reducedMotion: 'final-frame',
  };
}

/** Returns the default export settings, deriving the scene name from the project name. */
export function defaultExportSettings(projectName: string): ExportSettings {
  return {
    codeType: 'css',
    format: 'embed-snippet',
    media: defaultMediaSettings(),
    runtimeDelivery: 'inline',
    sceneName: toSceneName(projectName),
    prefixIds: true,
    minify: false,
    responsive: true,
    accessibleTitle: '',
  };
}

/** Returns the easing applied to newly created keyframes. */
export function defaultEasing(): Easing {
  return { type: 'linear' };
}

/** Returns the initial editor-only state. */
export function defaultEditorState(): EditorState {
  return {
    selection: { elementIds: [], keyframeIds: [] },
    playhead: 0,
    isPlaying: false,
    stageZoom: 1,
    stagePan: { x: 0, y: 0 },
    timelineZoom: 120,
    timelineScroll: 0,
    autoKey: true,
    snapping: true,
    expandedElementIds: [],
    hoveredElementId: null,
  };
}

/**
 * Converts free text into a valid CSS class fragment: lower-case ASCII letters, digits and single
 * hyphens, at most 32 characters, falling back to "scene" when nothing usable remains.
 */
export function toSceneName(text: string): string {
  const name = text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, MAX_SCENE_NAME_LENGTH)
    .replace(/-+$/, '');
  return name === '' ? 'scene' : name;
}

/** Creates a project holding an empty SVG, for tests and the store's initial state. */
export function createEmptyProject(name: string): Project {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: createId('proj'),
    name,
    createdAt: now,
    updatedAt: now,
    svg: {
      source: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"></svg>',
      viewBox: { x: 0, y: 0, width: 100, height: 100 },
      fileName: '',
    },
    elements: {},
    rootIds: [],
    tracks: [],
    scene: defaultSceneSettings(),
    export: defaultExportSettings(name),
  };
}
